import React, { useState, useEffect, useRef } from 'react';
import { Card, Collapse, Typography, Spin, Alert, Button, Space, message, Select, Divider, Checkbox, List, Tag, Input } from 'antd';
import {
  FileTextOutlined,
  ReloadOutlined,
  DownloadOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  GlobalOutlined,
  PlusCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import axios from '../api';
import { useAuth } from '../context/AuthContext';
// import io from 'socket.io-client'; // Temporarily disabled

const { Panel } = Collapse;
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

/**
 * AI Action Plan Component
 * Displays AI-generated CARE360 action plans with collapsible sections
 */
const AIActionPlan = ({ patientId, visible, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [actionPlan, setActionPlan] = useState(null);
  const [proposedOrders, setProposedOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [additionalContext, setAdditionalContext] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingActionPlan, setSavingActionPlan] = useState(false);
  const [actionPlanSaved, setActionPlanSaved] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [usePolling, setUsePolling] = useState(false);

  useEffect(() => {
    if (proposedOrders.length > 0) {
      setSelectedOrders(proposedOrders.map((_, index) => index));
    }
  }, [proposedOrders]);

  /**
   * Determine order type based on content analysis
   */
  const determineOrderTypeFromContent = (orderText) => {
    const text = orderText.toLowerCase();

    // Laboratory tests
    const labKeywords = [
      'xét nghiệm', 'máu', 'nước tiểu', 'phân', 'glucose', 'cholesterol', 'hba1c',
      'lipid', 'creatinine', 'urea', 'ast', 'alt', 'bilirubin', 'hemoglobin',
      'wbc', 'rbc', 'platelet', 'esr', 'crp', 'tsh', 'vitamin', 'hormone',
      'công thức máu', 'chức năng gan', 'chức năng thận', 'điện giải', 'marker',
      'test', 'lab', 'blood', 'urine', 'serum', 'plasma', 'panel', 'cbc',
      'metabolic panel', 'liver panel', 'lipid panel', 'basic metabolic', 'comprehensive metabolic',
      'complete blood count', 'differential', 'egfr', 'gfr', 'calculation', 'ferritin',
      'microalbumin', 'urinalysis', 'thyroid function', 'free t4', 'hscrp'
    ];

    // Imaging studies
    const imagingKeywords = [
      'x-quang', 'siêu âm', 'ct', 'mri', 'chụp', 'scan', 'echo', 'doppler',
      'x-ray', 'ultrasound', 'tomography', 'resonance', 'echocardiogram',
      'mammography', 'endoscopy', 'colonoscopy', 'ekg', 'ecg', 'holter'
    ];

    // Referrals
    const referralKeywords = [
      'tư vấn', 'chuyên khoa', 'hội chẩn', 'khám chuyên khoa', 'gửi khám',
      'referral', 'consult', 'specialist', 'cardiology', 'neurology',
      'ophthalmology', 'dermatology', 'orthopedic', 'psychiatry', 'psychology',
      'nephrology', 'endocrinology', 'gastroenterology', 'pulmonology',
      'sleep medicine', 'nutrition', 'dietitian', 'physical therapy'
    ];

    // Nursing/Patient Education
    const nursingKeywords = [
      'hướng dẫn', 'giáo dục', 'theo dõi', 'đo huyết áp', 'đo đường huyết',
      'ghi nhật ký', 'tự theo dõi', 'monitoring', 'education', 'instruction',
      'guidance', 'self-care', 'home monitoring', 'patient education'
    ];

    // Vaccinations
    const vaccinationKeywords = [
      'tiêm phòng', 'vaccine', 'vaccination', 'immunization', 'cúm', 'covid',
      'phế cầu', 'uốn ván', 'hpv', 'hepatitis', 'influenza', 'pneumococcal',
      'booster', 'tdap', 'tetanus', 'diphtheria', 'pertussis', 'mmr', 'varicella'
    ];

    // Medications
    const medicationKeywords = [
      'thuốc', 'medication', 'prescription', 'drug', 'tablet', 'capsule',
      'injection', 'insulin', 'metformin', 'aspirin', 'statin', 'ace inhibitor'
    ];

    // Check each category
    if (labKeywords.some(keyword => text.includes(keyword))) {
      return 'Laboratory';
    }

    if (imagingKeywords.some(keyword => text.includes(keyword))) {
      return 'Radiology';
    }

    if (referralKeywords.some(keyword => text.includes(keyword))) {
      return 'Referral';
    }

    if (vaccinationKeywords.some(keyword => text.includes(keyword))) {
      return 'Vaccine';
    }

    if (medicationKeywords.some(keyword => text.includes(keyword))) {
      return 'Medication';
    }

    if (nursingKeywords.some(keyword => text.includes(keyword))) {
      return 'Nursing';
    }

    // Default fallback
    return 'Other';
  };

  /**
   * Transform AI-generated order format to backend-expected format
   */
  const transformAIOrderToBackendFormat = (aiOrder, patientId) => {
    console.log('[AIActionPlan] Transforming AI order:', aiOrder);

    // Check authentication using AuthContext
    if (!isAuthenticated || !user) {
      console.error('[AIActionPlan] User not authenticated:', { isAuthenticated, user });
      throw new Error('User not authenticated - please log in again');
    }

    if (!user._id && !user.id) {
      console.error('[AIActionPlan] No user ID found:', user);
      throw new Error('User not authenticated - please log in again');
    }

    // Determine order type based on AI order structure
    let orderType = 'Other';
    let orderDetails = {};

    // Handle different AI order formats
    if (aiOrder.type) {
      // Format: { type: 'lab', description: '...', frequency: '...', system: '...' }
      const typeMapping = {
        'lab': 'Laboratory',
        'imaging': 'Radiology',
        'clinic': 'Referral',
        'preventive': 'Preventive Screening',
        'nursing': 'Nursing',
        'referral': 'Referral',
        'medication': 'Medication'
      };

      orderType = typeMapping[aiOrder.type] || 'Other';
      orderDetails = {
        description: aiOrder.description,
        instructions: aiOrder.description,
        system: aiOrder.system
      };

      if (aiOrder.type === 'lab') {
        orderDetails.testName = aiOrder.description;
        orderDetails.specimenType = 'Blood'; // Default
      } else if (aiOrder.type === 'imaging') {
        orderDetails.studyDescription = aiOrder.description;
        orderDetails.reasonForStudy = aiOrder.system;
      } else if (aiOrder.type === 'referral') {
        orderDetails.specialty = aiOrder.system;
        orderDetails.reasonForReferral = aiOrder.description;
      }

    } else if (aiOrder.name) {
      // Format: { name: '...', description: '...', frequency: '...', system: '...' }
      // or { name: '...', frequency: '...', indication: '...' }
      orderType = 'Other';
      orderDetails = {
        description: aiOrder.description || aiOrder.name,
        instructions: aiOrder.description || aiOrder.indication || aiOrder.name,
        system: aiOrder.system
      };

    } else if (aiOrder.order) {
      // Format: { order: '...', indication: '...' } or { order: '...', frequency: '...' } or { order: '...', system: '...' }
      // Intelligently determine order type based on content
      orderType = determineOrderTypeFromContent(aiOrder.order);
      console.log(`[AIActionPlan] Detected order type: "${orderType}" for order: "${aiOrder.order}"`);
      orderDetails = {
        description: aiOrder.order,
        instructions: aiOrder.indication || aiOrder.order,
        system: aiOrder.system
      };
    }

    // Add specific fields based on detected order type for UI display
    if (orderType === 'Laboratory') {
      orderDetails.testName = orderDetails.description;
      orderDetails.specimenType = 'Blood'; // Default
    } else if (orderType === 'Radiology') {
      orderDetails.studyDescription = orderDetails.description;
      orderDetails.reasonForStudy = orderDetails.system || 'Clinical indication';
    } else if (orderType === 'Referral') {
      orderDetails.referredToProvider = orderDetails.system || 'Specialist';
      orderDetails.reasonForReferral = orderDetails.description;
    }

    // Parse frequency to backend format
    let frequency = 'One-time';
    const frequencyText = aiOrder.frequency || aiOrder.order || '';

    if (frequencyText) {
      const freq = frequencyText.toLowerCase();
      if (freq.includes('mỗi ngày') || freq.includes('hàng ngày') || freq.includes('daily')) {
        frequency = 'Daily';
      } else if (freq.includes('mỗi tuần') || freq.includes('hàng tuần') || freq.includes('weekly')) {
        frequency = 'Weekly';
      } else if (freq.includes('mỗi tháng') || freq.includes('hàng tháng') || freq.includes('monthly')) {
        frequency = 'Monthly';
      } else if (freq.includes('mỗi năm') || freq.includes('hàng năm') || freq.includes('annually')) {
        frequency = 'Annually';
      } else if (freq.includes('6 tháng') || freq.includes('6 months')) {
        frequency = 'Every 6 months';
      } else if (freq.includes('3 tháng') || freq.includes('3 months')) {
        frequency = 'Every 3 months';
      } else if (freq.includes('1 tháng') || freq.includes('1 month')) {
        frequency = 'Monthly';
      }
    }

    // Set dates - default to 1 year duration
    const startDate = new Date();
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);

    const backendOrder = {
      orderType,
      frequency,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      status: 'Pending',
      patient: patientId,
      orderingProvider: user._id || user.id,
      orderDetails
    };

    // Validate required fields
    const requiredFields = ['orderType', 'frequency', 'startDate', 'endDate', 'patient', 'orderingProvider'];
    const missingFields = requiredFields.filter(field => !backendOrder[field]);

    if (missingFields.length > 0) {
      console.error('[AIActionPlan] Missing required fields:', missingFields);
      console.error('[AIActionPlan] Backend order:', backendOrder);
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    console.log('[AIActionPlan] Transformed to backend format:', backendOrder);
    return backendOrder;
  };

  /**
   * Polling fallback mechanism for when socket connection fails
   */
  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    console.log(`[AIActionPlan] Starting polling for job: ${jobId}`);
    let pollCount = 0;
    const maxPolls = 150; // Maximum 5 minutes of polling (150 * 2 seconds)

    pollingIntervalRef.current = setInterval(async () => {
      if (!jobId) {
        console.log('[AIActionPlan] No jobId, stopping polling');
        clearInterval(pollingIntervalRef.current);
        return;
      }

      pollCount++;
      if (pollCount > maxPolls) {
        console.log('[AIActionPlan] Polling timeout reached, stopping');
        setError('Request timeout. Please try again.');
        setLoading(false);
        clearInterval(pollingIntervalRef.current);
        return;
      }

      try {
        console.log(`[AIActionPlan] Polling job status for: ${jobId} (attempt ${pollCount})`);
        const response = await axios.get(`/ai-action-plan/jobs/${jobId}`);

        if (response.data.success) {
          const job = response.data.data;
          console.log(`[AIActionPlan] Job status: ${job.status}`);
          setJobStatus(job.status);

          if (job.status === 'completed') {
            console.log('[AIActionPlan] Job completed via polling');
            console.log('[AIActionPlan] Job result structure:', job.result);

            if (job.result && job.result.actionPlan) {
              console.log('[AIActionPlan] Setting action plan:', job.result.actionPlan);
              console.log('[AIActionPlan] Setting proposed orders:', job.result.orders);
              console.log('[AIActionPlan] Sample order structure:', job.result.orders?.[0]);
              setActionPlan(job.result.actionPlan);
              setProposedOrders(job.result.orders || []);
            } else {
              console.error('[AIActionPlan] Job completed but no action plan in result:', job.result);
              setError('Action plan generation completed but no content received');
            }
            setLoading(false);
            clearInterval(pollingIntervalRef.current);
          } else if (job.status === 'failed') {
            console.error('[AIActionPlan] Job failed via polling:', job.error);
            setError(job.error || 'Action plan generation failed');
            setLoading(false);
            clearInterval(pollingIntervalRef.current);
          }
          // Continue polling for 'pending' and 'running' statuses
        } else {
          console.error('[AIActionPlan] Polling response not successful:', response.data);
        }
      } catch (error) {
        console.error('[AIActionPlan] Polling error:', error);
        // Continue polling unless it's a critical error
        if (error.response?.status === 404) {
          setError('Job not found. Please try generating the action plan again.');
          setLoading(false);
          clearInterval(pollingIntervalRef.current);
        } else if (error.response?.status >= 500) {
          console.log('[AIActionPlan] Server error during polling, will retry');
        }
        // For other errors, continue polling
      }
    }, 2000); // Poll every 2 seconds
  };

  useEffect(() => {
    if (jobId) {
      console.log(`[AIActionPlan] Setting up connection for job: ${jobId}`);
      console.log(`[AIActionPlan] API URL: ${process.env.REACT_APP_API_URL}`);

      // For now, skip socket connection and go directly to polling
      // since we know socket.io has namespace issues in production
      console.log('[AIActionPlan] Using polling method due to known socket.io issues');
      setUsePolling(true);
      startPolling();
    }

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        console.log(`[AIActionPlan] Cleaning up polling interval for job: ${jobId}`);
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [jobId]);

  /**
   * Generate action plan by calling the AI service with automatic retry logic
   */
  const generateActionPlan = async (isRetry = false) => {
    if (!patientId) {
      message.error('Patient ID is required to generate action plan');
      return;
    }

    console.log(`[AIActionPlan] Starting action plan generation for patient: ${patientId}, language: ${selectedLanguage}, isRetry: ${isRetry}`);
    setLoading(true);
    setError(null);
    setJobStatus('pending');
    setActionPlanSaved(false); // Reset saved state when generating new plan

    try {
      const requestPayload = {
        language: selectedLanguage
      };

      // Include additional context if provided
      if (additionalContext && additionalContext.trim()) {
        requestPayload.additionalContext = additionalContext.trim();
        console.log(`[AIActionPlan] Including additional context (${additionalContext.trim().length} chars)`);
      }
      const response = await axios.post(`/patients/${patientId}/ai-action-plan`, requestPayload);

      console.log('[AIActionPlan] API response:', response.data);

      if (response.data.success) {
        const jobId = response.data.data.jobId;
        console.log(`[AIActionPlan] Job created with ID: ${jobId}`);
        setJobId(jobId);
      } else {
        throw new Error(response.data.message || 'Failed to start action plan generation');
      }
    } catch (error) {
      console.error('[AIActionPlan] Error starting action plan generation:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to start action plan generation';
      setError(errorMessage);
      setLoading(false);
    }
  };

  /**
   * Retry action plan generation (manual retry by user)
   */
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    generateActionPlan(true); // Pass true to indicate this is a manual retry
  };

  /**
   * Create selected orders from the AI-generated plan
   */
  const handleCreateOrders = async () => {
    if (selectedOrders.length === 0) {
      message.warning('No orders selected to create.');
      return;
    }

    // Check authentication before proceeding
    console.log('[AIActionPlan] Authentication check:', { isAuthenticated, user: user ? { id: user._id || user.id, username: user.username, role: user.role } : null });

    if (!isAuthenticated || !user) {
      console.error('[AIActionPlan] Authentication failed:', { isAuthenticated, user });
      message.error('You must be logged in to create orders. Please log in and try again.');
      return;
    }

    setSaving(true);
    const ordersToCreate = selectedOrders.map(index => proposedOrders[index]);
    let successCount = 0;
    let errorCount = 0;

    const hideLoading = message.loading(`Creating ${ordersToCreate.length} orders...`, 0);

    for (const order of ordersToCreate) {
      try {
        console.log('[AIActionPlan] Processing order:', order);

        // Transform AI order format to backend-expected format
        const orderPayload = transformAIOrderToBackendFormat(order, patientId);

        console.log('[AIActionPlan] Transformed order payload:', orderPayload);

        await axios.post(`/patients/${patientId}/orders`, orderPayload);
        successCount++;
      } catch (error) {
        console.error('Error creating order:', error);
        console.error('Error details:', error.response?.data);
        errorCount++;
      }
    }

    hideLoading();
    setSaving(false);

    if (successCount > 0) {
      message.success(`${successCount} orders created successfully.`);
    }

    if (errorCount > 0) {
      message.error(`${errorCount} orders failed to create. Please check the console for details.`);
    }

    if (successCount > 0 && errorCount === 0) {
      onClose(); // Close the modal on full success
    }
  };

  /**
   * Save action plan to the Action Plans management system
   */
  const saveActionPlan = async () => {
    if (!actionPlan || !patientId) {
      message.error(
        selectedLanguage === 'vietnamese'
          ? 'Không có kế hoạch hành động để lưu'
          : 'No action plan available to save'
      );
      return;
    }

    setSavingActionPlan(true);

    try {
      console.log('[AIActionPlan] Saving action plan to Action Plans management...');
      console.log('[AIActionPlan] Action plan structure:', actionPlan);
      console.log('[AIActionPlan] Action plan rawContent preview:', actionPlan?.rawContent?.substring(0, 500));
      console.log('[AIActionPlan] Action plan sections:', actionPlan?.sections);
      console.log('[AIActionPlan] Proposed orders:', proposedOrders);

      // Parse sections from the action plan content
      const parsedSections = parseSections(actionPlan.rawContent);
      console.log('[AIActionPlan] Parsed sections for saving:', parsedSections);

      // Prepare the action plan data in the format expected by the backend
      // Simplify the structure to match what the backend expects
      const actionPlanData = {
        actionPlan: {
          rawContent: actionPlan.rawContent,
          content: actionPlan.rawContent, // Some backends might expect 'content' instead
          sections: parsedSections,
          wordCount: actionPlan.wordCount,
          generatedAt: actionPlan.generatedAt
        },
        orders: proposedOrders || [],
        language: selectedLanguage,
        additionalContext: additionalContext && additionalContext.trim() ? additionalContext.trim() : null,
        generatedAt: new Date().toISOString(),
        patientId: patientId
      };

      console.log('[AIActionPlan] Sending actionPlanData:', {
        hasActionPlan: !!actionPlanData.actionPlan,
        hasRawContent: !!actionPlanData.actionPlan?.rawContent,
        hasSections: !!actionPlanData.actionPlan?.sections,
        sectionsCount: actionPlanData.actionPlan?.sections?.length,
        ordersCount: actionPlanData.orders?.length,
        rawContentLength: actionPlanData.actionPlan?.rawContent?.length,
        payloadSize: JSON.stringify(actionPlanData).length
      });

      // Check payload size (warn if > 1MB)
      const payloadSize = JSON.stringify(actionPlanData).length;
      if (payloadSize > 1024 * 1024) {
        console.warn(`[AIActionPlan] Large payload size: ${(payloadSize / 1024 / 1024).toFixed(2)}MB`);
      }

      const response = await axios.post(`/patients/${patientId}/ai-action-plan/save`, {
        actionPlanData,
        language: selectedLanguage
      }, {
        timeout: 60000, // 60 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success(
          selectedLanguage === 'vietnamese'
            ? 'Kế hoạch hành động đã được lưu thành công vào hệ thống quản lý'
            : 'Action plan saved successfully to Action Plans management'
        );
        console.log('[AIActionPlan] Action plan saved successfully:', response.data.data);

        // Mark as saved
        setActionPlanSaved(true);

        // Optionally close the modal or provide additional feedback
        if (response.data.data?.actionPlan?.id) {
          console.log(`[AIActionPlan] Saved action plan ID: ${response.data.data.actionPlan.id}`);
        }
      } else {
        throw new Error(response.data.message || 'Failed to save action plan');
      }
    } catch (error) {
      console.error('[AIActionPlan] Error saving action plan:', error);
      console.error('[AIActionPlan] Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });

      // Log the specific error message from backend
      if (error.response?.data) {
        console.error('[AIActionPlan] Backend error response:', error.response.data);
      }

      let errorMessage = selectedLanguage === 'vietnamese'
        ? 'Không thể lưu kế hoạch hành động'
        : 'Failed to save action plan';

      if (error.response?.status === 501) {
        errorMessage = selectedLanguage === 'vietnamese'
          ? 'Tính năng quản lý kế hoạch hành động đang được phát triển'
          : 'Action Plan Management feature is still in development';
      } else if (error.response?.status === 400) {
        errorMessage = selectedLanguage === 'vietnamese'
          ? 'Dữ liệu kế hoạch hành động không hợp lệ'
          : 'Invalid action plan data format';
      } else if (error.response?.status === 413) {
        errorMessage = selectedLanguage === 'vietnamese'
          ? 'Dữ liệu quá lớn để lưu'
          : 'Request payload too large';
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = selectedLanguage === 'vietnamese'
          ? 'Không có quyền truy cập'
          : 'Authentication failed';
      } else if (error.response?.status >= 500) {
        errorMessage = selectedLanguage === 'vietnamese'
          ? 'Lỗi máy chủ, vui lòng thử lại sau'
          : 'Server error, please try again later';
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = selectedLanguage === 'vietnamese'
          ? 'Lỗi kết nối mạng'
          : 'Network connection error';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    } finally {
      setSavingActionPlan(false);
    }
  };

  /**
   * Download action plan as text file
   */
  const downloadActionPlan = () => {
    if (!actionPlan || !actionPlan.actionPlan) {
      message.warning('No action plan available to download');
      return;
    }

    const content = actionPlan.actionPlan.rawContent;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const languageSuffix = selectedLanguage === 'vietnamese' ? '_VI' : '_EN';
    link.download = `CARE360_Action_Plan_Patient_${patientId}_${new Date().toISOString().split('T')[0]}${languageSuffix}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    message.success(
      selectedLanguage === 'vietnamese'
        ? 'Kế hoạch hành động đã được tải xuống thành công'
        : 'Action plan downloaded successfully'
    );
  };

  /**
   * Parse sections from action plan content
   */
  const parseSections = (content) => {
    if (!content) return [];

    const sections = [];
    const lines = content.split('\n');
    let currentSection = null;
    let currentContent = [];

    lines.forEach(line => {
      if (line.startsWith('## ')) {
        // Save previous section
        if (currentSection) {
          sections.push({
            title: currentSection,
            content: currentContent.join('\n').trim()
          });
        }
        
        // Start new section
        currentSection = line.replace('## ', '').trim();
        currentContent = [];
      } else if (currentSection) {
        currentContent.push(line);
      }
    });

    // Add last section
    if (currentSection) {
      sections.push({
        title: currentSection,
        content: currentContent.join('\n').trim()
      });
    }

    return sections;
  };

  const renderOrderDetails = (order) => {
    // Add defensive programming to handle undefined order
    if (!order) {
      console.error('[AIActionPlan] renderOrderDetails called with undefined order');
      return <Text type="danger">Invalid order data</Text>;
    }

    // Handle new simplified format: { order: "...", indication: "..." }
    if (order.order && order.indication) {
      return (
        <div>
          <Text strong>{order.order}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">Order</Tag>
          </div>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ display: 'block' }}>
              <strong>Indication:</strong> {order.indication}
            </Text>
          </div>
        </div>
      );
    }

    // Handle newest format: { name: "...", description: "...", frequency: "...", system: "..." }
    if (order.name && order.description) {
      return (
        <div>
          <Text strong>{order.name}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">{order.system || 'Order'}</Tag>
            {order.frequency && <Tag color="green">{order.frequency}</Tag>}
          </div>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ display: 'block' }}>
              {order.description}
            </Text>
          </div>
        </div>
      );
    }

    // Handle format with indication: { name: "...", frequency: "...", indication: "..." }
    if (order.name && order.indication) {
      return (
        <div>
          <Text strong>{order.name}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">Order</Tag>
            {order.frequency && <Tag color="green">{order.frequency}</Tag>}
          </div>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ display: 'block' }}>
              <strong>Indication:</strong> {order.indication}
            </Text>
          </div>
        </div>
      );
    }

    // Handle format with type: { type: "...", description: "...", frequency: "...", system: "..." }
    if (order.type && order.description) {
      const getTypeColor = (type) => {
        const colorMap = {
          'lab': 'red',
          'imaging': 'blue',
          'clinic': 'green',
          'preventive': 'orange',
          'nursing': 'purple',
          'referral': 'cyan',
          'medication': 'magenta'
        };
        return colorMap[type] || 'blue';
      };

      const getTypeLabel = (type) => {
        const labelMap = {
          'lab': 'Lab Test',
          'imaging': 'Imaging',
          'clinic': 'Clinical',
          'preventive': 'Preventive',
          'nursing': 'Nursing',
          'referral': 'Referral',
          'medication': 'Medication'
        };
        return labelMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
      };

      return (
        <div>
          <Text strong>{order.description}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color={getTypeColor(order.type)}>{getTypeLabel(order.type)}</Tag>
            {order.frequency && <Tag color="green">{order.frequency}</Tag>}
            {order.system && <Tag color="geekblue">{order.system}</Tag>}
          </div>
        </div>
      );
    }

    // Handle format with indication: { name: "...", frequency: "...", indication: "..." }
    if (order.name && order.indication) {
      return (
        <div>
          <Text strong>{order.name}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">Order</Tag>
            {order.frequency && <Tag color="green">{order.frequency}</Tag>}
          </div>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ display: 'block' }}>
              <strong>Indication:</strong> {order.indication}
            </Text>
          </div>
        </div>
      );
    }

    // Handle format with system: { order: "...", system: "..." } (with or without frequency)
    if (order.order && order.system) {
      return (
        <div>
          <Text strong>{order.order}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">Order</Tag>
            {order.frequency && <Tag color="green">{order.frequency}</Tag>}
            <Tag color="purple">{order.system}</Tag>
          </div>
        </div>
      );
    }

    // Handle simple format: { order: "...", frequency: "..." } (without indication or system)
    if (order.order && order.frequency && !order.indication && !order.system) {
      return (
        <div>
          <Text strong>{order.order}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">Order</Tag>
            <Tag color="green">{order.frequency}</Tag>
          </div>
        </div>
      );
    }

    // Handle simple format: { order: "..." } (only order text)
    if (order.order && !order.frequency && !order.indication && !order.system) {
      return (
        <div>
          <Text strong>{order.order}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">Order</Tag>
          </div>
        </div>
      );
    }

    // Handle legacy structured format: { orderType, orderDetails, frequency }
    const { orderType, orderDetails, frequency } = order;

    if (!orderDetails) {
      console.error('[AIActionPlan] orderDetails is undefined for order:', order);
      return (
        <div>
          <Text strong>{orderType || 'Unknown Order'}</Text>
          <div>
            <Tag color="blue">{orderType || 'Unknown'}</Tag>
            <Tag>{frequency || 'N/A'}</Tag>
          </div>
        </div>
      );
    }

    let title = '';
    const details = [];

    switch (orderType) {
      case 'Medication':
        title = orderDetails.medicationName || 'Medication';
        if (orderDetails.dose) details.push(`Dose: ${orderDetails.dose}${orderDetails.unit || ''}`);
        if (orderDetails.route) details.push(`Route: ${orderDetails.route}`);
        if (orderDetails.duration) details.push(`Duration: ${orderDetails.duration}`);
        if (orderDetails.precautions) details.push(`Precautions: ${orderDetails.precautions}`);
        break;
      case 'Laboratory':
        title = orderDetails.testName || 'Lab Test';
        if (orderDetails.specimenType) details.push(`Specimen: ${orderDetails.specimenType}`);
        if (orderDetails.collectionInstructions) details.push(`Instructions: ${orderDetails.collectionInstructions}`);
        break;
      case 'Radiology':
        title = orderDetails.studyDescription || 'Imaging Study';
        if (orderDetails.reasonForStudy) details.push(`Reason: ${orderDetails.reasonForStudy}`);
        if (orderDetails.imagingCenter) details.push(`Center: ${orderDetails.imagingCenter}`);
        break;
      case 'Referral':
        title = `Referral to ${orderDetails.specialty || 'Specialist'}`;
        if (orderDetails.referredToProvider) details.push(`Provider: ${orderDetails.referredToProvider}`);
        if (orderDetails.reasonForReferral) details.push(`Reason: ${orderDetails.reasonForReferral}`);
        break;
      case 'Lifestyle':
        title = `Lifestyle: ${orderDetails.lifestyleCategory || 'Recommendation'}`;
        if (orderDetails.lifestyleGeneralInstructions) details.push(orderDetails.lifestyleGeneralInstructions);
        break;
      default:
        title = (orderDetails && orderDetails.description) || orderType || 'Unknown Order';
    }

    return (
      <div>
        <Text strong>{title}</Text>
        <div>
          <Tag color="blue">{orderType}</Tag>
          <Tag>{frequency}</Tag>
        </div>
        {details.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {details.map((detail, i) => (
              <Text type="secondary" key={i} style={{ display: 'block' }}>
                {detail}
              </Text>
            ))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Render action plan sections
   */
  const renderActionPlan = () => {
    console.log('[AIActionPlan] renderActionPlan called, actionPlan:', actionPlan);
    if (!actionPlan) return null;

    const sections = parseSections(actionPlan.rawContent);
    console.log('[AIActionPlan] Parsed sections:', sections);

    if (sections.length === 0) {
      return (
        <Alert
          message="Action Plan Generated"
          description={
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <ReactMarkdown>{actionPlan.rawContent}</ReactMarkdown>
            </div>
          }
          type="success"
          showIcon
        />
      );
    }

    return (
      <div>
        {/* Action Plan Header */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
            <Text strong>CARE360 Action Plan</Text>

          </Space>
        </div>

        {/* Collapsible Sections */}
        <Collapse 
          defaultActiveKey={['0']} 
          ghost
          style={{ backgroundColor: '#fafafa' }}
        >
          {sections.map((section, index) => (
            <Panel 
              header={
                <Text strong style={{ fontSize: '14px' }}>
                  {section.title}
                </Text>
              } 
              key={index}
            >
              <div style={{ 
                backgroundColor: 'white', 
                padding: '16px', 
                borderRadius: '6px',
                border: '1px solid #f0f0f0'
              }}>
                <ReactMarkdown>{section.content}</ReactMarkdown>
              </div>
            </Panel>
          ))}
        </Collapse>

        <Divider />
        <Title level={4}>Proposed Orders</Title>
        <List
          dataSource={proposedOrders}
          renderItem={(order, index) => (
            <List.Item>
              <Checkbox
                checked={selectedOrders.includes(index)}
                onChange={e => {
                  if (e.target.checked) {
                    setSelectedOrders([...selectedOrders, index]);
                  } else {
                    setSelectedOrders(selectedOrders.filter(i => i !== index));
                  }
                }}
              >
                {renderOrderDetails(order)}
              </Checkbox>
            </List.Item>
          )}
        />
      </div>
    );
  };

  if (!visible) return null;

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined />
          <span>
            {selectedLanguage === 'vietnamese'
              ? 'Kế Hoạch Hành Động CARE360 Một Năm'
              : 'CARE360 One-Year Action Plan'
            }
          </span>
        </Space>
      }
      extra={
        <Space>
          {actionPlan && (
            <>
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={handleCreateOrders}
                loading={saving}
                disabled={selectedOrders.length === 0}
                size="small"
                title="Create the selected orders in the patient's chart"
              >
                {`Create Selected Orders (${selectedOrders.length})`}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadActionPlan}
                size="small"
              >
                {selectedLanguage === 'vietnamese' ? 'Tải xuống' : 'Download'}
              </Button>
              <Button
                icon={actionPlanSaved ? <CheckCircleOutlined /> : <SaveOutlined />}
                onClick={saveActionPlan}
                loading={savingActionPlan}
                disabled={savingActionPlan || actionPlanSaved}
                size="small"
                type={actionPlanSaved ? "default" : "primary"}
                title={actionPlanSaved
                  ? (selectedLanguage === 'vietnamese' ? 'Kế hoạch đã được lưu' : 'Action plan saved')
                  : (selectedLanguage === 'vietnamese' ? 'Lưu kế hoạch vào hệ thống quản lý' : 'Save action plan to Action Plans management system')
                }
              >
                {actionPlanSaved
                  ? (selectedLanguage === 'vietnamese' ? 'Đã lưu' : 'Saved')
                  : (selectedLanguage === 'vietnamese' ? 'Lưu kế hoạch' : 'Save Plan')
                }
              </Button>
            </>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRetry}
            loading={loading}
            disabled={loading}
            size="small"
          >
            {selectedLanguage === 'vietnamese'
              ? (retryCount > 0 ? 'Thử lại' : 'Tạo lại')
              : (retryCount > 0 ? 'Retry' : 'Regenerate')
            }
          </Button>
          {onClose && (
            <Button onClick={onClose} size="small">
              {selectedLanguage === 'vietnamese' ? 'Đóng' : 'Close'}
            </Button>
          )}
        </Space>
      }
      style={{ marginTop: 16 }}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>
              {jobStatus === 'pending' && (selectedLanguage === 'vietnamese' ? 'Đang khởi tạo...' : 'Initializing...')}
              {jobStatus === 'running' && (selectedLanguage === 'vietnamese' ? 'Đang tạo kế hoạch...' : 'Generating plan...')}
              {jobStatus !== 'pending' && jobStatus !== 'running' && (selectedLanguage === 'vietnamese' ? 'Đang tạo kế hoạch hành động CARE360 cá nhân hóa...' : 'Generating personalized CARE360 action plan...')}
              {usePolling && (
                <span style={{ color: '#1890ff', fontSize: '11px', marginLeft: '8px' }}>
                  (using polling)
                </span>
              )}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {selectedLanguage === 'vietnamese'
                ? 'Quá trình này có thể mất vài phút. Bạn có thể đóng cửa sổ này và quay lại sau.'
                : 'This may take a few minutes. You can close this window and come back later.'
              }
            </Text>
          </div>
        </div>
      )}

      {error && (
        <Alert
          message={
            selectedLanguage === 'vietnamese'
              ? 'Tạo Kế Hoạch Hành Động Thất Bại'
              : 'Action Plan Generation Failed'
          }
          description={
            <div>
              <p>{error}</p>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleRetry}
                style={{ marginTop: 8 }}
              >
                {selectedLanguage === 'vietnamese' ? 'Thử Lại' : 'Try Again'}
              </Button>
            </div>
          }
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && !error && !actionPlan && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: 16 }} />
          <Title level={4} style={{ color: '#666' }}>
            {selectedLanguage === 'vietnamese' ? 'Tạo Kế Hoạch Hành Động CARE360' : 'Generate CARE360 Action Plan'}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {selectedLanguage === 'vietnamese'
              ? 'Tạo kế hoạch hành động cá nhân hóa 1 năm dựa trên dữ liệu EMR của bệnh nhân, bao gồm khuyến nghị theo tầng, mục tiêu SMART và can thiệp lâm sàng.'
              : 'Create a personalized 1-year action plan based on this patient\'s EMR data, including tier recommendations, SMART goals, and clinical interventions.'
            }
          </Text>

          {/* Language Selection */}
          <div style={{ marginBottom: 24 }}>
            <Space direction="vertical" size="middle">
              <div>
                <GlobalOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                <Text strong>
                  {selectedLanguage === 'vietnamese' ? 'Chọn ngôn ngữ:' : 'Select Language:'}
                </Text>
              </div>
              <Select
                value={selectedLanguage}
                onChange={setSelectedLanguage}
                style={{ width: 200 }}
                size="large"
              >
                <Option value="english">
                  🇺🇸 English
                </Option>
                <Option value="vietnamese">
                  🇻🇳 Tiếng Việt
                </Option>
              </Select>
            </Space>
          </div>

          {/* Additional Patient Context */}
          <div style={{ marginBottom: 24, textAlign: 'left', maxWidth: 600, margin: '0 auto 24px auto' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                <Text strong>
                  {selectedLanguage === 'vietnamese'
                    ? 'Thông tin bổ sung về bệnh nhân (tùy chọn):'
                    : 'Additional Patient Context (Optional):'}
                </Text>
              </div>
              <TextArea
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder={selectedLanguage === 'vietnamese'
                  ? 'Nhập thông tin bổ sung như: chi tiết nhập viện gần đây, kết quả chẩn đoán hình ảnh, thay đổi lối sống, cập nhật tiền sử gia đình, yếu tố xã hội, thay đổi thuốc, mối quan tâm của bệnh nhân...'
                  : 'Enter supplementary information such as: recent hospitalization details, imaging findings, lifestyle changes, family history updates, social determinants, medication changes, patient concerns...'
                }
                rows={4}
                maxLength={2000}
                showCount
                style={{ width: '100%' }}
              />
              <Text type="secondary" style={{ fontSize: '12px', textAlign: 'left' }}>
                {selectedLanguage === 'vietnamese'
                  ? 'Thông tin này sẽ giúp AI tạo ra kế hoạch hành động phù hợp và cá nhân hóa hơn cho bệnh nhân.'
                  : 'This information will help the AI generate more relevant and personalized action plans for the patient.'}
              </Text>
            </Space>
          </div>

          <Space direction="vertical" align="center">
            {additionalContext && additionalContext.trim() && (
              <div style={{
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                color: '#1890ff'
              }}>
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                {selectedLanguage === 'vietnamese'
                  ? `Bao gồm thông tin bổ sung (${additionalContext.trim().length} ký tự)`
                  : `Including additional context (${additionalContext.trim().length} chars)`}
              </div>
            )}
            <Button
              type="primary"
              size="large"
              icon={<FileTextOutlined />}
              onClick={generateActionPlan}
              loading={loading}
            >
              {selectedLanguage === 'vietnamese' ? 'Tạo Kế Hoạch Hành Động' : 'Generate Action Plan'}
            </Button>
          </Space>
        </div>
      )}

      {!loading && !error && actionPlan && renderActionPlan()}
    </Card>
  );
};

export default AIActionPlan;
